import { ValidationObserver, ValidationProvider } from "vee-validate";
import { MaintenanceMixin } from '@/mixins/Maintenance';
import { EventLocationsMixin } from "@/mixins/EventLocations";
import {MaintenancePriority} from "@/enums/MaintenancePriority";
import {MaintenanceRequestStatus} from "@/enums/MaintenanceRequestStatus";
import {dateFormats} from "@/consts/DateTimeFormats";
import moment from "moment/moment";
import {ResidentStatus} from "@/enums/ResidentStatus";
import { MaintenanceTypes } from '@/enums/MaintenanceTypes';
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";
import { MaintenanceChecklist } from '@/models/maintenance/MaintenanceChecklist';
import { sortItemsAsc } from "@/utils/sortItemsAsc";

export default {
  name: "MaintenanceRequestEditDlg",
  components: {
    ValidationObserver,
    ValidationProvider
  },
  mixins: [MaintenanceMixin, EventLocationsMixin],
  data() {
    return {
      dialog: false,
      loading: false,
      request: null,
      selectedTechnician: null,
      estimatedDeliveryDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10),
      menu: false,
      reqType: {
        id: "",
        typeId: "",
        name: "",
        category: "",
        categoryId: "",
        department: "",
        icon: ""
      },
      newMaintRes: null,
      selectedTypes: []
    }
  },
  computed: {
    MaintenanceTypes() {
      return MaintenanceTypes;
    },
    maintenanceTypes() {
      return this.$store.state.maintenanceModule.maintenanceTypes;
    },
    residentsList() {
      return this.$store.state.residentModule.residentsShortData.filter(resident => resident.status !== ResidentStatus.Departed);
    },
    maintenanceCategories() {
      return this.$store.getters.maintenanceCategories;
    },
    maintenancePriority() {
      return Object.values(MaintenancePriority);
    },
    isInProgress() {
      return this.request.status === MaintenanceRequestStatus.InProgress;
    },
    maintenanceTechnicians() {
      return sortItemsAsc([...this.$store.state.userModule.maintenanceUsers, ...this.$store.state.userModule.maintenanceVendors.map(x => { return {...x, isVendor: true} })], 'name')
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    estimatedDeliveryDateFormatted() {
      const dateFormat = dateFormats.find(x => x.format === this.facility.dateFormat);
      return moment(this.estimatedDeliveryDate).format(dateFormat ? dateFormat.name : dateFormats[0].name);
    },
    currentUser(){
        return this.$store.state.authModule.user
    },
    canAddEventLocation(){
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Calendar_Events_AddLocation)
    }
  },
  watch: {
    newMaintRes(res) {
      if(res != null) {
        this.request.residentId = res.id;
        this.request.residentName = res.name;
        this.request.roomName = res.roomName;
      }
    },
    reqType(requestType) {
      if(requestType) {
        this.request.name = requestType?.name;
        this.request.category = requestType?.category;
        this.request.categoryId = requestType?.categoryId;
        this.request.department = requestType?.department;
        this.request.typeId = requestType?.id;
        this.request.icon = requestType?.icon;
      }
    },
    'request.locationId'(locationId) {
      if(locationId != null) {
        this.request.location = this.eventLocations?.find(x => x.id === locationId)?.name ?? '';
      }
    },
    'request.wingId'(wingId) {
      if(wingId != null) {
        this.request.wing = this.wings?.find(x => x.id === wingId)?.name ?? '';
      }
    },
  },
  methods: {
    async open(request) {
      this.dialog = true;
      this.request = JSON.parse(JSON.stringify(request));

      this.selectedTechnician = request.assigneeEmail ?? null;
      this.estimatedDeliveryDate = this.request.estimatedCompletionDate ?? this.estimatedDeliveryDate;

      this.setRequestType();
      this.setResident();
    },
    close() {
      this.dialog = false;
      this.selectedTechnician = null;
    },
    setRequestType() {
      if(this.request.typeId) {
        const type = this.maintenanceTypes.find(item => item.id === this.request.typeId);
        this.reqType = { ...type };
      }
    },
    setResident() {
      if(this.request.residentId) {
        const resident = this.residentsList.find(item => item.id === this.request.residentId);
        this.newMaintRes = { ...resident };
      }
    },
    clearRes() {
      this.request.residentId = null;
      this.request.residentName = null;
      this.request.roomName = null;

      this.newMaintRes = null;
    },
    async submit() {
      this.loading = true;
      const request = JSON.parse(JSON.stringify(this.request));
      const maintenanceTechnician = this.maintenanceTechnicians.find(x => x.email === this.selectedTechnician);

      request.assignee = maintenanceTechnician?.name ?? null;
      request.assigneeId = maintenanceTechnician?.id ?? null;
      request.assigneeEmail = maintenanceTechnician?.email ?? null;
      request.assigneePhoneNumber = maintenanceTechnician?.phoneNumber ?? null;
      request.estimatedCompletionDate = this.estimatedDeliveryDate ?? null;

      delete request.id;
      delete request.assignedDate;
      delete request.dateTime;
      delete request.completedDateTime;

      await this.$store.dispatch("editMaintenanceRequest", {
        id: this.request.id,
        updates: request,
      }).then(() => {
        this.$emit('onMaintenanceRequestChanged')
        this.$toast.open({
          type: "success",
          message: this.$t("MaintenanceView.MaintenanceRequestSuccessfullyEdit")
        })
        this.loading = false;
        this.close()
      })
    },
    addItemForChecklist() {
      if (!Array.isArray(this.request.checklist)) {
        this.$set(this.request, 'checklist', []);
      }

      const label = `Item ${this.request.checklist.length + 1}`;
      const item = new MaintenanceChecklist(label, false).toObject();

      this.request.checklist.push(item);
    },
    removeItemFromChecklist(index) {
      this.request.checklist.splice(index, 1);
      this.request.checklist.forEach((item, index) => {
        if (item.label.includes('Item')) {
          item.label = `Item ${index + 1}`;
        }
      });
    }
  }
}